import { useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { api } from '@acme/client';
import { teamActiveAtom, upgradeModalAtom, userAtom, userSubscriptionAtom } from '../store/user';
import { selectAtom } from 'jotai/utils';
import { chatSessionsAtom } from '../store/chat';
import { deepEqual } from '@acme/util';
import { notesAtom } from '../store/note';

const chatSessionsNumsAtom = selectAtom(chatSessionsAtom, chatSessions => {
    const totalSessions = chatSessions.length;
    const totalSyncSessions = chatSessions.filter(session => session.isSync).length;
    return { totalSessions, totalSyncSessions };
}, deepEqual)

const notesNumsAtom = selectAtom(notesAtom, notes => {
    const totalNotes = notes.length || 0;
    const totalSyncNotes = notes.filter(note => note.isSync)?.length || 0;
    return { totalNotes, totalSyncNotes };
}, deepEqual)


export const useUserSubscription = () => {
    const user = useAtomValue(userAtom);
    const teamActive = useAtomValue(teamActiveAtom);
    const { totalSessions, totalSyncSessions } = useAtomValue(chatSessionsNumsAtom);
    const { totalNotes, totalSyncNotes } = useAtomValue(notesNumsAtom)
    const [subscription, setSubscription] = useAtom(userSubscriptionAtom);
    const { data, isLoading, refetch: onRefetch } = api.user.subscription.useQuery(
        { teamId: teamActive?.id || '' },
        { enabled: !!teamActive?.id, staleTime: 8000 }
    );

    const planName = useMemo(() => {
        return subscription?.planPurchase?.plan?.name || data?.planPurchase?.plan?.name || '';
    }, [data?.planPurchase?.plan?.name, subscription?.planPurchase?.plan?.name]);

    useEffect(() => {
        if (data && !isLoading) {
            setSubscription(data);
        }
    }, [data, isLoading, setSubscription]);

    const isSubscribe: null | boolean = useMemo(() => {
        if (!subscription && !teamActive && isLoading) return null;
        if (subscription?.id || data?.id) return true;
        return false;
    }, [data?.id, isLoading, subscription, teamActive]);

    const isAllowAddChatSession = useMemo(() => {
        // user not logged in max 2
        if (!user?.id) {
            return {
                allow: totalSessions < 5,
                allowSync: false,
                message: `You have reached the limit of ${totalSessions} chats, signin to add more.`
            };
        }
        // user logged in max 5 and max sync 1
        if (user?.id && !isSubscribe) {
            return {
                allow: totalSessions < 20 || totalSyncSessions <= 1,
                allowSync: totalSyncSessions <= 1,
                message: `You have reached the limit of ${totalSessions} chats. Max 1 sync chat & 5 chats.`,
            }
        };
        // if lifetime
        if (planName === 'Lifetime') {
            return {
                allow: totalSessions < 300 || totalSyncSessions < 5,
                allowSync: totalSyncSessions < 5,
                message: `You have reached the limit of ${totalSyncSessions} sync chats. Max 2 sync chat.`
            }
        }
        // if subscribed unlimited
        if (isSubscribe) return { allow: true, allowSync: true, message: '' };
        return { allow: false, allowSync: false, message: '' }
    }, [isSubscribe, planName, totalSessions, totalSyncSessions, user?.id]);

    const isAllowAddNote = useMemo(() => {
        // user not logged in max 2
        if (!user?.id) return {
            allow: totalNotes <= 2,
            allowSync: false,
            message: `You have reached the limit of ${totalNotes} notes, signin to add more.`
        };
        // user logged in max 5 and max sync 1
        if (user?.id && !isSubscribe) {
            return {
                allow: totalNotes <= 5 && totalSyncNotes <= 1,
                allowSync: totalSyncNotes < 1,
                message: `You have reached the limit of ${totalNotes} notes.`
            }
        };
        // if lifetime
        if (planName === 'Lifetime') {
            return {
                allow: totalNotes < 200 && totalSyncNotes <= 2,
                allowSync: totalSyncNotes < 2,
                message: `You have reached the limit of ${totalSyncSessions} sync notes. Max 2 sync notes.`
            }
        }
        // if subscribed unlimited
        if (isSubscribe) return { allow: true, allowSync: true, message: '' };
        return { allow: false, allowSync: false, message: '' }
    }, [isSubscribe, planName, totalNotes, totalSyncNotes, totalSyncSessions, user?.id]);

    // =============================================================================
    // FEATURE ACCESS CONTROLS
    // =============================================================================

    /** Check if user can use favorites feature */
    const isAllowFavorites = useMemo(() => {
        // Free users cannot use favorites
        if (!isSubscribe) {
            return {
                allow: false,
                message: 'Upgrade to unlock favorites feature'
            };
        }
        return { allow: true, message: '' };
    }, [isSubscribe]);

    /** Check if user can use web search and advanced tools */
    const isAllowTools = useMemo(() => {
        // Free users cannot use web search and tools
        if (!isSubscribe) {
            return {
                allow: false,
                message: 'Upgrade to unlock web search and AI tools'
            };
        }
        return { allow: true, message: '' };
    }, [isSubscribe]);

    /** Check if user can use MCP servers */
    const isAllowMCP = useMemo(() => {
        // Free users cannot use MCP
        if (!isSubscribe) {
            return {
                allow: false,
                message: 'Upgrade to unlock MCP server integrations'
            };
        }
        return { allow: true, message: '' };
    }, [isSubscribe]);

    /** Check if user can use agents */
    const isAllowAgents = useMemo(() => {
        // Free users cannot use agents
        if (!isSubscribe) {
            return {
                allow: false,
                message: 'Upgrade to unlock AI agents'
            };
        }
        return { allow: true, message: '' };
    }, [isSubscribe]);

    /** Check if user can use cloud features (sync, API key storage) */
    const isAllowCloud = useMemo(() => {
        // User not logged in - no cloud
        if (!user?.id) {
            return {
                allow: false,
                allowApiKeyStorage: false,
                message: 'Sign in to use cloud features'
            };
        }
        // Free users cannot use cloud features (API key storage)
        if (!isSubscribe) {
            return {
                allow: false,
                allowApiKeyStorage: false,
                message: 'Upgrade to unlock cloud sync and API key storage'
            };
        }
        // Lifetime users have limited cloud sync
        if (planName === 'Lifetime') {
            return {
                allow: true,
                allowApiKeyStorage: true,
                maxSyncChats: 5,
                maxSyncNotes: 2,
                message: 'Lifetime plan: limited cloud sync (5 chats, 2 notes)'
            };
        }
        // Full subscribers have unlimited cloud
        return { allow: true, allowApiKeyStorage: true, message: '' };
    }, [user?.id, isSubscribe, planName]);

    return {
        planName,
        subscription,
        isSubscribe,
        isLoggedIn: !!user?.id,
        isAllowAddChatSession,
        isAllowAddNote,
        // Feature access
        isAllowFavorites,
        isAllowTools,
        isAllowMCP,
        isAllowAgents,
        isAllowCloud,
        isLoading,
        onRefetch
    };
};